import { Head, Link, usePage } from "@inertiajs/react"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"
import { useState } from "react"
import MatchLayout from "@/layouts/match-layout"
import { Pencil, FileText, Loader2, Sparkles, Video } from "lucide-react"

// ─── Types ─────────────────────────────────────────────────────────────────

interface Team {
  id: string
  name: string
  short_name: string
  color: string
  score: number
}

interface GraphStat {
  label: string
  home_value: number
  away_value: number
  type: "bar" | "circular"
  unit?: string
}

interface LineStat {
  label: string
  home_value: number | string
  away_value: number | string
}

interface StatsSection {
  title: string
  graphs: GraphStat[]
  lines: LineStat[]
}

interface Player {
  number: number
  name: string
  position: string
}

interface LineupSide {
  starting_xv: Player[]
  substitutes: Player[]
}

interface TimelineEvent {
  minute: number
  team: "home" | "away" | null
  event: string
  player: string | null
  shirt_number: number | null
  detail: string | null
}

interface MatchInfo {
  competition: string
  weather: string
  pitch: string
  attendance: string | null
}

interface MatchReportSummary {
  id: number
  status: "pending" | "generating" | "completed" | "failed"
}

interface Match {
  id: string
  date: string
  kickoff: string
  venue: string
  referee: string
  status: string
  home_team: Team
  away_team: Team
  lineups: { home: LineupSide; away: LineupSide }
  timeline: TimelineEvent[]
  match_info: MatchInfo
  stats_sections: StatsSection[]
  video_url: string | null
  report: MatchReportSummary | null
}

interface MatchDashboardProps {
  match: Match
}

// ─── Event helpers ─────────────────────────────────────────────────────────

const EVENT_ICONS: Record<string, string> = {
  try: "🏉",
  conversion: "✅",
  conversion_miss: "❌",
  penalty: "🟡",
  yellow_card: "🟨",
  red_card: "🟥",
  halftime: "⏸",
  fulltime: "🏁",
}

const EVENT_LABELS: Record<string, string> = {
  try: "Try",
  conversion: "Conversion",
  conversion_miss: "Conversion Missed",
  penalty: "Penalty Goal",
  yellow_card: "Yellow Card",
  red_card: "Red Card",
  halftime: "Half Time",
  fulltime: "Full Time",
}

// ─── Shared: Team Badge ────────────────────────────────────────────────────

function TeamBadge({ shortName, color, size = 44 }: { shortName: string; color: string; size?: number }) {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-md font-extrabold text-white shadow-sm select-none"
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        fontSize: size * 0.28,
      }}
    >
      {shortName}
    </div>
  )
}

// ─── Shared: Score Header ──────────────────────────────────────────────────

function ScoreHeader({ home, away }: { home: Team; away: Team }) {
  return (
    <div className="mx-auto flex max-w-xl items-center justify-center gap-4 py-4 sm:gap-8">
      <div className="flex flex-col items-center gap-1.5">
        <TeamBadge shortName={home.short_name} color={home.color} size={44} />
        <span className="text-sm font-semibold sm:text-base">{home.name}</span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-3xl font-black tabular-nums sm:text-4xl">{home.score}</span>
        <span className="text-lg text-muted-foreground">–</span>
        <span className="text-3xl font-black tabular-nums sm:text-4xl">{away.score}</span>
      </div>

      <div className="flex flex-col items-center gap-1.5">
        <TeamBadge shortName={away.short_name} color={away.color} size={44} />
        <span className="text-sm font-semibold sm:text-base">{away.name}</span>
      </div>
    </div>
  )
}

// ─── Tab: Statistics ───────────────────────────────────────────────────────

function BarGraph({ stat, homeColor, awayColor }: { stat: GraphStat; homeColor: string; awayColor: string }) {
  const max = Math.max(stat.home_value, stat.away_value, 1)
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="flex w-full items-end justify-center gap-3" style={{ height: 80 }}>
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs font-semibold text-foreground">{stat.home_value}</span>
          <div className="w-8 rounded-t-sm" style={{ height: `${(stat.home_value / max) * 56}px`, backgroundColor: homeColor, minHeight: 4 }} />
        </div>
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs font-semibold text-foreground">{stat.away_value}</span>
          <div className="w-8 rounded-t-sm" style={{ height: `${(stat.away_value / max) * 56}px`, backgroundColor: awayColor, minHeight: 4 }} />
        </div>
      </div>
      <span className="text-center text-[11px] leading-tight text-muted-foreground">{stat.label}</span>
    </div>
  )
}

function CircularGraph({ stat, homeColor, awayColor }: { stat: GraphStat; homeColor: string; awayColor: string }) {
  const size = 64
  const sw = 5
  const r = (size - sw) / 2
  const c = 2 * Math.PI * r
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-2.5">
        {[
          { pct: stat.home_value / 100, color: homeColor, val: stat.home_value },
          { pct: stat.away_value / 100, color: awayColor, val: stat.away_value },
        ].map((ring, i) => (
          <div key={i} className="relative">
            <svg width={size} height={size} className="-rotate-90">
              <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={sw} className="text-muted/20" />
              <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ring.color} strokeWidth={sw}
                strokeDasharray={c} strokeDashoffset={c * (1 - ring.pct)} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[11px] font-bold" style={{ color: ring.color }}>{ring.val}{stat.unit}</span>
            </div>
          </div>
        ))}
      </div>
      <span className="text-center text-[11px] leading-tight text-muted-foreground">{stat.label}</span>
    </div>
  )
}

function HorizontalBar({ stat, homeColor, awayColor }: { stat: LineStat; homeColor: string; awayColor: string }) {
  const parse = (v: number | string) => typeof v === "number" ? v : parseFloat(String(v))
  const homeNum = parse(stat.home_value)
  const awayNum = parse(stat.away_value)
  const max = Math.max(homeNum, awayNum, 1)
  const pct = 42
  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <div className="flex items-center">
        <div className="flex flex-1 justify-end">
          <div className="h-2 rounded-l-sm" style={{ width: `${(homeNum / max) * pct}%`, backgroundColor: homeColor, minHeight: 4 }} />
        </div>
        <div className="w-28 shrink-0 text-center text-[11px] text-muted-foreground">{stat.label}</div>
        <div className="flex flex-1 justify-start">
          <div className="h-2 rounded-r-sm" style={{ width: `${(awayNum / max) * pct}%`, backgroundColor: awayColor, minHeight: 4 }} />
        </div>
      </div>
      <div className="flex items-center">
        <div className="flex flex-1 justify-end"><span className="text-xs font-semibold" style={{ color: homeColor }}>{stat.home_value}</span></div>
        <div className="w-28 shrink-0" />
        <div className="flex flex-1 justify-start"><span className="text-xs font-semibold" style={{ color: awayColor }}>{stat.away_value}</span></div>
      </div>
    </div>
  )
}

function StatSection({ section, homeColor, awayColor }: { section: StatsSection; homeColor: string; awayColor: string }) {
  const hasG = section.graphs.length > 0
  const hasL = section.lines.length > 0
  return (
    <div className="rounded-lg border bg-card shadow-sm">
      <div className="border-b px-4 py-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-foreground">{section.title}</h2>
      </div>
      <div className="p-4">
        {hasG && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {section.graphs.map((s, i) =>
              s.type === "circular" ? <CircularGraph key={i} stat={s} homeColor={homeColor} awayColor={awayColor} /> : <BarGraph key={i} stat={s} homeColor={homeColor} awayColor={awayColor} />,
            )}
          </div>
        )}
        {hasG && hasL && <div className="my-4 border-t" />}
        {hasL && (
          <div className="divide-y divide-border">
            {section.lines.map((s, i) => <HorizontalBar key={i} stat={s} homeColor={homeColor} awayColor={awayColor} />)}
          </div>
        )}
      </div>
    </div>
  )
}

function StatsTab({ match }: { match: Match }) {
  const home = match.home_team
  const away = match.away_team
  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
        <div className="flex items-center gap-2"><div className="h-3 w-3 rounded-sm" style={{ backgroundColor: home.color }} /><span className="font-medium">{home.name}</span></div>
        <div className="flex items-center gap-2"><div className="h-3 w-3 rounded-sm" style={{ backgroundColor: away.color }} /><span className="font-medium">{away.name}</span></div>
      </div>
      {match.stats_sections.map((s, i) => <StatSection key={i} section={s} homeColor={home.color} awayColor={away.color} />)}
    </div>
  )
}

// ─── Tab: Lineups ──────────────────────────────────────────────────────────

function PlayerRow({ player, color }: { player: Player; color: string }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: color }}>{player.number}</span>
      <span className="text-sm">{player.name}</span>
      <span className="ml-auto text-xs font-medium text-muted-foreground">{player.position}</span>
    </div>
  )
}

function LineupColumn({ team, lineup }: { team: Team; lineup: LineupSide }) {
  return (
    <div className="flex-1">
      <div className="mb-4 flex items-center gap-2">
        <TeamBadge shortName={team.short_name} color={team.color} size={28} />
        <h3 className="font-semibold">{team.name}</h3>
      </div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Starting XV</div>
      <div className="divide-y divide-border">
        {lineup.starting_xv.map((p) => <PlayerRow key={p.number} player={p} color={team.color} />)}
      </div>
      <div className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Substitutes</div>
      <div className="divide-y divide-border">
        {lineup.substitutes.map((p) => <PlayerRow key={p.number} player={p} color={team.color} />)}
      </div>
    </div>
  )
}

function LineupsTab({ match }: { match: Match }) {
  return (
    <div className="flex flex-col gap-6 md:flex-row md:gap-10">
      <LineupColumn team={match.home_team} lineup={match.lineups.home} />
      <div className="hidden md:block md:w-px md:bg-border" />
      <div className="block md:hidden h-px bg-border" />
      <LineupColumn team={match.away_team} lineup={match.lineups.away} />
    </div>
  )
}

// ─── Tab: Timeline ─────────────────────────────────────────────────────────

function TimelineEntry({ evt, home, away }: { evt: TimelineEvent; home: Team; away: Team }) {
  const isHome = evt.team === "home"
  const isAway = evt.team === "away"
  const isSeparator = evt.event === "halftime" || evt.event === "fulltime"
  const team = isHome ? home : isAway ? away : null
  const icon = EVENT_ICONS[evt.event] ?? "•"
  const label = EVENT_LABELS[evt.event] ?? evt.event

  if (isSeparator) {
    return (
      <div className="flex items-center gap-3 py-3">
        <div className="h-px flex-1 bg-border" />
        <div className="flex items-center gap-2 rounded-full border bg-muted px-4 py-1.5 text-xs font-semibold">
          <span>{icon}</span>
          <span>{evt.minute}&apos;</span>
          <span className="text-muted-foreground">—</span>
          <span>{label}</span>
          {evt.detail && <span className="text-muted-foreground">{evt.detail}</span>}
        </div>
        <div className="h-px flex-1 bg-border" />
      </div>
    )
  }

  return (
    <div className={cn("flex items-center gap-3 py-2", isHome ? "md:flex-row" : "md:flex-row-reverse md:text-right")}>
      <div className="flex w-10 shrink-0 items-center justify-center text-sm font-bold tabular-nums text-muted-foreground">{evt.minute}&apos;</div>
      <div className="flex w-6 shrink-0 justify-center text-base">{icon}</div>
      {team && <TeamBadge shortName={team.short_name} color={team.color} size={22} />}
      <div className="flex flex-col">
        <span className="text-sm font-medium">
          {evt.player || (evt.shirt_number ? `#${evt.shirt_number}` : null)}
        </span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      {evt.detail && <span className="text-xs italic text-muted-foreground">{evt.detail}</span>}
    </div>
  )
}

function TimelineTab({ match }: { match: Match }) {
  return (
    <div className="flex flex-col divide-y divide-border">
      {match.timeline.map((evt, i) => <TimelineEntry key={i} evt={evt} home={match.home_team} away={match.away_team} />)}
    </div>
  )
}

// ─── Tab: Match Info ───────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value || "—"}</span>
    </div>
  )
}

function MatchInfoTab({ match }: { match: Match }) {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-bold uppercase tracking-wider">Match Details</h3>
        </div>
        <div className="divide-y divide-border px-4">
          <InfoRow label="Competition" value={match.match_info.competition} />
          <InfoRow label="Date" value={match.date} />
          <InfoRow label="Kick-off" value={match.kickoff} />
          <InfoRow label="Venue" value={match.venue} />
          <InfoRow label="Referee" value={match.referee} />
        </div>
      </div>
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-bold uppercase tracking-wider">Conditions</h3>
        </div>
        <div className="divide-y divide-border px-4">
          <InfoRow label="Weather" value={match.match_info.weather} />
          <InfoRow label="Pitch" value={match.match_info.pitch} />
          <InfoRow label="Attendance" value={match.match_info.attendance} />
        </div>
      </div>
    </div>
  )
}

// ─── Tab: Video ────────────────────────────────────────────────────────────

function getYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

function VideoTab({ match }: { match: Match }) {
  if (!match.video_url) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <svg className="mb-3 h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
        </svg>
        <p className="text-sm font-medium">No video available for this match</p>
      </div>
    )
  }

  const youtubeId = getYouTubeId(match.video_url)

  if (youtubeId) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <div className="relative w-full" style={{ paddingTop: "56.25%" /* 16:9 */ }}>
          <iframe
            className="absolute inset-0 h-full w-full rounded-lg"
            src={`https://www.youtube-nocookie.com/embed/${youtubeId}`}
            title="Match video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    )
  }

  // Self-hosted / direct video URL
  return (
    <div className="mx-auto w-full max-w-4xl">
      <video
        className="w-full rounded-lg"
        controls
        preload="metadata"
      >
        <source src={match.video_url} />
        Your browser does not support the video tag.
      </video>
    </div>
  )
}

// ─── Tab: Report ───────────────────────────────────────────────────────────

function ReportTab({ match }: { match: Match }) {
  const report = match.report
  const hasVideo = !!match.video_url

  if (!hasVideo && !report) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
          <FileText className="h-7 w-7 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">No video available for AI analysis</p>
        <p className="mt-1 text-xs text-muted-foreground">Add a video URL to this match to enable AI-powered stat extraction</p>
      </div>
    )
  }

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Sparkles className="h-7 w-7 text-primary" />
        </div>
        <p className="text-sm font-medium">AI Video Analysis Available</p>
        <p className="mt-1 text-xs text-muted-foreground">Auto-draft match events & stats from the video</p>
        <a
          href={`/matches/${match.id}/match_report`}
          className="mt-4 flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Video className="h-4 w-4" />
          Analyze Video
        </a>
      </div>
    )
  }

  if (report.status === "pending" || report.status === "generating") {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary" />
        <p className="text-sm font-medium">Analyzing video…</p>
        <p className="mt-1 text-xs text-muted-foreground">Extracting frames and running AI vision analysis</p>
      </div>
    )
  }

  return (
    <a
      href={`/matches/${match.id}/match_report`}
      className="flex items-center gap-4 rounded-lg border bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <Video className="h-6 w-6 text-primary" />
      </div>
      <div className="flex-1">
        <p className="font-semibold">AI Video Analysis — Draft</p>
        <p className="text-sm text-muted-foreground">Events, scores & stats extracted from match video</p>
      </div>
      <span className="rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
        View Results →
      </span>
    </a>
  )
}

// ─── Tab definitions ───────────────────────────────────────────────────────

type TabId = "lineups" | "timeline" | "stats" | "video" | "info" | "report"
const TABS: { id: TabId; label: string }[] = [
  { id: "lineups", label: "Lineups" },
  { id: "timeline", label: "Timeline" },
  { id: "stats", label: "Statistics" },
  { id: "video", label: "Video" },
  { id: "report", label: "Video AI" },
  { id: "info", label: "Match Info" },
]

// ─── Main Page ─────────────────────────────────────────────────────────────

export default function MatchDashboard({ match }: MatchDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabId>("stats")
  const home = match.home_team
  const away = match.away_team
  const { auth } = usePage().props as { auth: { user?: { role: string } | null } }
  const isStaff = auth?.user?.role === "admin" || auth?.user?.role === "coach"

  return (
    <>
      <Head title={`${home.name} vs ${away.name}`} />
      {/* Scoreboard */}
      <div className="border-b bg-card">
          <div className="flex items-center justify-between mx-auto max-w-4xl px-4">
            <ScoreHeader home={home} away={away} />
            {isStaff && (
              <Link
                href={`/staff/matches/${match.id}/edit`}
                className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Link>
            )}
          </div>
          <div className="flex items-center justify-center gap-4 pb-3 text-xs text-muted-foreground">
            <span>{match.date}</span>
            <span>•</span>
            <span>{match.kickoff}</span>
            <span>•</span>
            <span>{match.venue}</span>
          </div>

          {/* Tab bar */}
          <nav className="border-t">
            <div className="mx-auto flex max-w-xl justify-center">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex-1 border-b-2 px-3 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors sm:text-sm",
                    activeTab === tab.id
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Content */}
        <div className="mx-auto max-w-4xl px-4 py-6">
          {activeTab === "stats" && <StatsTab match={match} />}
          {activeTab === "lineups" && <LineupsTab match={match} />}
          {activeTab === "timeline" && <TimelineTab match={match} />}
          {activeTab === "video" && <VideoTab match={match} />}
          {activeTab === "report" && <ReportTab match={match} />}
          {activeTab === "info" && <MatchInfoTab match={match} />}
        </div>
    </>
  )
}

MatchDashboard.layout = (page: ReactNode) => <MatchLayout>{page}</MatchLayout>
